import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Container,
  Paper,
  CircularProgress,
  Alert,
} from '@mui/material';
import AdminDashboardIcon from '@mui/icons-material/Dashboard';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ScheduleIcon from '@mui/icons-material/Schedule';
import InventoryIcon from '@mui/icons-material/Inventory';
import HistoryIcon from '@mui/icons-material/History';
import PaymentIcon from '@mui/icons-material/Payment';
import ZigRateManagement from '../components/admin/ZigRateManagement';
import ClientManagement from '../components/admin/ClientManagement';
import MetricsDashboard from '../components/admin/MetricsDashboard';
import OperationsPanel from '../components/admin/OperationsPanel';
import ProductManagement from '../components/admin/ProductManagement';
import AuditLogs from '../components/admin/AuditLogs';
import PaymentManagement from '../components/admin/PaymentManagement';
import AdminRoute from '../components/AdminRoute';
import { useDecodedToken } from '../utils/useDecodedToken';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`admin-tabpanel-${index}`}
      aria-labelledby={`admin-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AdminDashboard: React.FC = () => {
  const user = useDecodedToken();
  
  // Check if user has ADMINISTRATOR or AUDIT role (roles that can see audit logs)
  const hasAuditAccess = (): boolean => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return false;
      
      const { jwtDecode } = require('jwt-decode');
      const decoded: any = jwtDecode(token);
      
      const realmRoles = decoded.realm_access?.roles || [];
      const resourceRoles: string[] = [];
      
      if (decoded.resource_access) {
        Object.values(decoded.resource_access).forEach((client: any) => {
          if (client.roles) {
            resourceRoles.push(...client.roles);
          }
        });
      }
      
      const allRoles = [...realmRoles, ...resourceRoles];
      return allRoles.some((role: string) => {
        const upperRole = role.toUpperCase();
        return upperRole === 'ADMINISTRATOR' || 
               upperRole === 'ROLE_ADMINISTRATOR' ||
               upperRole === 'AUDIT' ||
               upperRole === 'ROLE_AUDIT';
      });
    } catch (error) {
      return false;
    }
  };
  
  const canViewAuditLogs = hasAuditAccess();
  
  // Check if there's a tab index stored in sessionStorage (from navigation)
  const getInitialTab = (): number => {
    const storedTab = sessionStorage.getItem('adminTabIndex');
    if (storedTab) {
      let tabIndex = parseInt(storedTab, 10);
      sessionStorage.removeItem('adminTabIndex'); // Clear after reading
      
      // If trying to access audit logs tab (6) but user doesn't have access, default to 0
      if (tabIndex === 6 && !canViewAuditLogs) {
        return 0;
      }
      
      // If trying to access Operations (tab 7 when Audit Logs visible, tab 6 when hidden) but Audit Logs is hidden, adjust
      if (tabIndex === 7 && !canViewAuditLogs) {
        tabIndex = 6;
      }
      
      return tabIndex;
    }
    return 0; // Default to Overview tab
  };

  const [tabValue, setTabValue] = useState(getInitialTab());

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <AdminRoute>
      <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
            <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
              Admin Dashboard
            </Typography>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="admin dashboard tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab
                icon={<AdminDashboardIcon />}
                iconPosition="start"
                label="Overview"
                id="admin-tab-0"
                aria-controls="admin-tabpanel-0"
              />
              <Tab
                icon={<InventoryIcon />}
                iconPosition="start"
                label="Products"
                id="admin-tab-1"
                aria-controls="admin-tabpanel-1"
              />
              <Tab
                icon={<PaymentIcon />}
                iconPosition="start"
                label="Payment Management"
                id="admin-tab-2"
                aria-controls="admin-tabpanel-2"
              />
              <Tab
                icon={<CurrencyExchangeIcon />}
                iconPosition="start"
                label="ZIG Rate Management"
                id="admin-tab-3"
                aria-controls="admin-tabpanel-3"
              />
              <Tab
                icon={<PeopleIcon />}
                iconPosition="start"
                label="Client Management"
                id="admin-tab-4"
                aria-controls="admin-tabpanel-4"
              />
              <Tab
                icon={<AssessmentIcon />}
                iconPosition="start"
                label="Metrics & Analytics"
                id="admin-tab-5"
                aria-controls="admin-tabpanel-5"
              />
              {canViewAuditLogs && (
                <Tab
                  icon={<HistoryIcon />}
                  iconPosition="start"
                  label="Audit Logs"
                  id="admin-tab-6"
                  aria-controls="admin-tabpanel-6"
                />
              )}
              <Tab
                icon={<ScheduleIcon />}
                iconPosition="start"
                label="Operations"
                id={`admin-tab-${canViewAuditLogs ? 7 : 6}`}
                aria-controls={`admin-tabpanel-${canViewAuditLogs ? 7 : 6}`}
              />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <MetricsDashboard />
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <ProductManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <PaymentManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <ZigRateManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <ClientManagement />
          </TabPanel>

          <TabPanel value={tabValue} index={5}>
            <MetricsDashboard detailed />
          </TabPanel>

          <TabPanel value={tabValue} index={canViewAuditLogs ? 7 : 6}>
            <OperationsPanel />
          </TabPanel>

          {canViewAuditLogs && (
            <TabPanel value={tabValue} index={6}>
              <AuditLogs />
            </TabPanel>
          )}
        </Paper>
      </Container>
    </AdminRoute>
  );
};

export default AdminDashboard;

